import { DateValue } from '@internationalized/date';
import { FilesAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { filesOutboxEventCountsQueryAtom } from '../_graphql/queryFilesOutboxEventCounts';
import { filesOutboxEventsQueryAtom } from '../_graphql/queryFilesOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type FilesAuditTab = 'events' | 'inbox';

export const $filesAuditTab = atom<FilesAuditTab>('events');

export const $filesAggregateTypeFilter = atom<FilesAggregateType | undefined>(
  undefined,
);

export const $filesAggregateIdFilter = atom<string | undefined>(undefined);

export const $filesPagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedFilesEventId = atom<string | undefined>(undefined);

export const $filesDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteFilesOutboxEvents = filesOutboxEventsQueryAtom((get) => {
  const aggregateType = get($filesAggregateTypeFilter);
  const aggregateId = get($filesAggregateIdFilter);
  const pagination = get($filesPagination);
  const dateRange = get($filesDateRangeFilter);

  return {
    variables: {
      aggregateTypes: aggregateType ? [aggregateType] : undefined,
      aggregateId,
      pagination,
      createdAtRange:
        dateRange?.start && dateRange?.end
          ? {
              start: dateToZonedDateTimeStart(dateRange.start),
              end: dateToZonedDateTimeEnd(dateRange.end),
            }
          : undefined,
    },
  };
});

export const $remoteFilesOutboxEventCounts = filesOutboxEventCountsQueryAtom();

export const useFilesAuditTab = () => {
  const [tab, setTab] = useAtom($filesAuditTab);
  return [tab, setTab] as const;
};

export const useFilesAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($filesAggregateTypeFilter);
  const [, setAggregateId] = useAtom($filesAggregateIdFilter);

  const setFilterAndResetId = (value: FilesAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useFilesAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($filesAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useFilesPagination = () => {
  const [pagination, setPagination] = useAtom($filesPagination);
  return [pagination, setPagination] as const;
};

export const useSelectedFilesEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedFilesEventId);
  return [selectedId, setSelectedId] as const;
};

export const useFilesDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($filesDateRangeFilter);
  return [filter, setFilter] as const;
};
